import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import {
  CheckIcon,
  ChevronDownIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  AddressComponentConfig,
  AddressComponentVisibility,
  AddressFormField,
  AddressPlaceType,
  FormAfterSubmission,
  FormCaptchaConfig,
  FormField,
  FormFieldType,
  formFieldTypes,
  MultiChoiceFormField,
  PictureChoiceFormField,
  PostSubmission,
  SelectFormField,
  SimpleForm,
} from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import Button from '@superfunnel/ui-core/v2/Button';
import Checkbox from '@superfunnel/ui-core/v2/Checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import {
  extractTextFromQuillTrimmed,
  formFieldUrlValidator,
} from '@superfunnel/utils';
import { Timestamp } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import countries from '../assets/countries.json';
import languages from '../assets/languages.json';
import phoneCodes from '../assets/phone-country-codes.json';
import { CSSTextArea } from '../components/FormFields/CSSTextArea';
import FileUploadInput from '../components/FormFields/FileUpload/FileUploadInput';
import QuillField from '../components/QuillField';
import SortableList from '../components/SortableList';
import { AuthContext } from '../contexts/AuthContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import {
  createForm as createFormApi,
  updateForm,
} from '../services/FormsService';
import { groupBy } from '../utils';
import {
  getFormFieldTypeIcon,
  getFormFieldTypeLabel,
} from '../utils/form-utils';

type EditableFormField = FormField & { _id: string };

const defaultForm = (): UpdatableForm => {
  return {
    name: 'Lead capture form',
    fields: [
      { _id: uid(8), id: 'name', label: 'Name', type: 'text' },
      {
        _id: uid(8),
        id: 'email',
        label: 'Email',
        type: 'email',
        required: true,
      },
      { _id: uid(8), id: 'phone', label: 'Phone', type: 'tel' },
      { _id: uid(8), id: 'message', label: 'Message', type: 'textArea' },
    ],
    buttonTitle: 'Send',
    postSubmission: { action: 'none' },
    captcha: { enabled: false },
  };
};

interface UpdatableForm {
  name?: string;
  fields?: EditableFormField[];
  buttonTitle?: string;
  buttonCSS?: Record<string, any>;
  postSubmission?: Partial<PostSubmission>;
  preActionText?: string;
  afterSubmission?: FormAfterSubmission;
  captcha?: FormCaptchaConfig;
  formModalTitle?: string;
}

interface Props {
  form?: SimpleForm;
  formId?: string;
  open: boolean;
  onClose: () => void;
}

export default function CreateFormModal({
  open,
  onClose,
  form,
  formId,
}: Props) {
  const { show } = useContext(SnackbarContext);
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);

  const [editId, setEditId] = useState<string>();
  const [data, setData] = useState<UpdatableForm>(() => defaultForm());
  const [creating, setCreating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<Error>();
  const [editingFields, setEditingFields] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    setData(
      form
        ? {
            name: form.name,
            fields: form.fields?.map((f) => ({ ...f, _id: uid(8) })),
            buttonTitle: form.button.title,
            buttonCSS: form.button.buttonCSS,
            postSubmission: form.postSubmission,
            preActionText: form.preActionText,
            afterSubmission: form.afterSubmission,
            captcha: form.captcha,
            formModalTitle: form.formModalTitle,
          }
        : defaultForm(),
    );
    setEditId(Date.now().toString());
    setCreating(false);
    setError(undefined);
  }, [open, form]);

  const createForm = useCallback(async () => {
    if (creating || !selectedAccount) return;
    setCreating(true);

    try {
      const form: SimpleForm = {
        name: data.name?.trim() ?? '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: firebaseUser?.uid ?? '',
        fields: (data.fields?.map((f) => {
          const newField = { ...f };
          if (newField._id) delete (newField as any)._id;
          return newField;
        }) ?? []) as FormField[],
        button: {
          title: data.buttonTitle?.trim() ?? '',
          ...(data.buttonCSS ? { buttonCSS: data.buttonCSS } : {}),
        },
        postSubmission: data.postSubmission as PostSubmission,
        status: 'active',
        preActionText: data.preActionText || '',
        afterSubmission: data.afterSubmission || {},
        captcha: data.captcha ?? { enabled: false },
        formType: 'simple',
        formModalTitle: data.formModalTitle || '',
      };
      if (!data.name?.trim()) {
        show('Name is required');
        return;
      }
      if (!data.buttonTitle?.trim()) {
        show('Button title is required');
        return;
      }
      if (!data.fields?.length) {
        show('Add at least one field');
        return;
      }
      if (data.fields?.find((f) => !f.id)) {
        show('One of the fields is missing an id');
        return;
      }
      if (
        Object.values(
          groupBy(data.fields?.map((f) => f.id) || [], (a) => a),
        ).some((x) => x.length > 1)
      ) {
        show('You have multiple fields with the same id');
        return;
      }

      if (formId) {
        await updateForm(selectedAccount.id, formId, form);
      } else {
        await createFormApi(selectedAccount.id, form);
      }
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    } finally {
      setCreating(false);
    }
  }, [
    creating,
    data.afterSubmission,
    data.buttonCSS,
    data.buttonTitle,
    data.captcha,
    data.fields,
    data.formModalTitle,
    data.name,
    data.postSubmission,
    data.preActionText,
    firebaseUser?.uid,
    formId,
    onClose,
    selectedAccount,
    show,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: creating,
        onClick: () => onClose(),
      },
      {
        title: formId ? 'Update' : 'Create',
        type: 'primary',
        loading: creating,
        onClick: () => createForm(),
      },
    ],
    [creating, formId, onClose, createForm],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="xl"
      title={formId ? 'Update form' : 'Create a new form'}
    >
      <div className="mt-0 flex flex-col gap-4 pb-12">
        <Input
          title="SimpleForm name"
          readOnly={creating}
          value={data.name}
          onChange={(e) => {
            setData((d) => {
              d.name = e.target.value;
              return { ...d };
            });
          }}
          validation={{
            status: !data.name && data.name !== undefined ? 'error' : undefined,
            message:
              !data.name && data.name !== undefined
                ? 'Name is required'
                : undefined,
          }}
        />

        <div className="flex flex-col gap-1">
          <div className="flex flex-row items-center justify-between">
            <FieldTitle title="Form fields" />
            <Button
              size="micro"
              variant="ghost"
              color="primary"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
            </Button>
          </div>

          <SortableList
            items={data.fields ?? []}
            itemClassName={'overflow-visible bg-white px-4 py-4 mx-1'}
            renderItemContent={(item) => {
              return (
                <FormFieldItem
                  field={item}
                  fields={data.fields || []}
                  readOnly={creating}
                  editting={editingFields[item._id]}
                  showAdvanced={showAdvanced}
                  onChange={(field) => {
                    setData((d) => ({
                      ...d,
                      fields: [
                        ...(d.fields ?? []).map(
                          (f) =>
                            ({
                              ...f,
                              ...(f._id === field._id ? field : {}),
                            }) as any,
                        ),
                      ],
                    }));
                  }}
                  onDelete={() => {
                    setData((d) => ({
                      ...d,
                      fields: [
                        ...(d.fields ?? []).filter((f) => f._id !== item._id),
                      ],
                    }));
                  }}
                  onEdit={() => {
                    setEditingFields((v) => ({
                      ...v,
                      [item._id]: true,
                    }));
                  }}
                  onDone={() => {
                    setEditingFields((v) => ({
                      ...v,
                      [item._id]: false,
                    }));
                  }}
                />
              );
            }}
            onChange={(items) => {
              setData((d) => ({
                ...d,
                fields: items,
              }));
            }}
          />

          <div className="flex justify-end">
            <Button
              onClick={() => {
                const newId = uid(8);
                setData((d) => ({
                  ...d,
                  fields: [
                    ...(d.fields ?? []),
                    { _id: newId, id: newId, type: 'text' as const, label: '' },
                  ],
                }));
                setEditingFields((v) => ({ ...v, [newId]: true }));
              }}
            >
              Add
            </Button>
          </div>
        </div>
        <Input
          title="Submit button title"
          readOnly={creating}
          value={data.buttonTitle}
          onChange={(e) => {
            setData((d) => {
              d.buttonTitle = e.target.value;
              return { ...d };
            });
          }}
          validation={{
            status:
              !data.buttonTitle && data.buttonTitle !== undefined
                ? 'error'
                : undefined,
            message:
              !data.buttonTitle && data.buttonTitle !== undefined
                ? 'Submit button title is required'
                : undefined,
          }}
        />
        <div className="flex flex-col justify-start gap-1">
          <FieldTitle title="After submission" />
          <Select
            value={data.postSubmission?.action}
            disabled={creating}
            onValueChange={(e: string) => {
              setData((d) => {
                let obj = d.postSubmission;
                if (!obj) obj = d.postSubmission = {};
                obj.action = e;
                return { ...d };
              });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="What will happen after submission" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Do nothing</SelectItem>
              <SelectItem value="openLink">Open link</SelectItem>
              <SelectItem value="openCalendly">Open Calendly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {data.postSubmission?.action === 'openCalendly' ? (
          <>
            <Input
              title="Calendly link"
              readOnly={creating}
              value={data.postSubmission.calendlyLink}
              onChange={(e) => {
                setData((d) => {
                  let obj = d.postSubmission;
                  if (!obj) obj = d.postSubmission = {};
                  obj.calendlyLink = e.target.value;
                  return { ...d };
                });
              }}
            />
          </>
        ) : (
          <></>
        )}
        {data.postSubmission?.action === 'openLink' ? (
          <>
            <Input
              title="Link"
              type="url"
              readOnly={creating}
              value={data.postSubmission?.link}
              onChange={(e) => {
                setData((d) => {
                  let obj = d.postSubmission;
                  if (!obj) obj = d.postSubmission = {};
                  obj.link = e.target.value;
                  return { ...d };
                });
              }}
              validation={formFieldUrlValidator(
                data.postSubmission?.link ?? '',
              )}
            />
          </>
        ) : (
          <></>
        )}
        <QuillField
          key={editId + '-form-modal-title'}
          title="Form modal title (optional)"
          readOnly={creating}
          value={data.formModalTitle}
          placeholder="Title displayed at the top of the form modal"
          onChange={(e) => {
            setData((d) => {
              const length = extractTextFromQuillTrimmed(e).length;
              d.formModalTitle = length ? (e as any) : null;
              return { ...d };
            });
          }}
        />
        <QuillField
          key={editId + '-pre-button'}
          title="Pre button agreement text (optional)"
          readOnly={creating}
          value={data.preActionText}
          placeholder="Example: by clicking here you agree to..."
          onChange={(e) => {
            setData((d) => {
              const length = extractTextFromQuillTrimmed(e).length;
              d.preActionText = length ? (e as any) : null;
              return { ...d };
            });
          }}
        />
        <QuillField
          key={editId + '-after-submission-title'}
          title="Thank you title"
          readOnly={creating}
          value={data.afterSubmission?.title}
          placeholder="Defaults to: Submitted"
          onChange={(e) => {
            setData((d) => {
              if (!d.afterSubmission) {
                d.afterSubmission = {};
              }

              const length = extractTextFromQuillTrimmed(e).length;
              d.afterSubmission.title = length ? (e as any) : null;
              return { ...d };
            });
          }}
        />

        {/* Advanced Settings */}
        <Collapsible>
          <CollapsibleTrigger className="group flex w-full items-center justify-between py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
            <span>Advanced</span>
            <ChevronDownIcon className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="data-[state=closed]:animate-collapse-up data-[state=open]:animate-collapse-down overflow-hidden">
            <div className="flex flex-col gap-4 pt-2">
              <div className="flex items-center space-x-3">
                <Switch
                  checked={data.captcha?.enabled}
                  onCheckedChange={(checked) => {
                    setData((d) => {
                      if (!d.captcha) {
                        d.captcha = { enabled: checked };
                      } else {
                        d.captcha.enabled = checked;
                      }
                      return { ...d };
                    });
                  }}
                />
                <Label className="text-sm font-medium">
                  Invisible reCAPTCHA
                </Label>
              </div>

              <CSSTextArea
                title="Button Custom CSS"
                className="h-32"
                value={data.buttonCSS}
                onChange={(value) => {
                  setData((d) => ({
                    ...d,
                    buttonCSS: value as Record<string, any> | undefined,
                  }));
                }}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create form'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}

export interface FormFieldItemProps {
  field: EditableFormField;
  fields: EditableFormField[];
  readOnly: boolean;
  editting?: boolean;
  showAdvanced?: boolean;
  onChange: (_: EditableFormField) => void;
  onDelete: () => void;
  onEdit: () => void;
  onDone: () => void;
}

export function FormFieldItem({
  field,
  fields,
  readOnly,
  editting,
  showAdvanced,
  onChange,
  onDelete,
  onEdit,
  onDone,
}: FormFieldItemProps) {
  if (!editting) {
    return (
      <div className="group/form-field flex w-full items-center gap-2">
        <span className="flex items-center gap-1">
          {getFormFieldTypeIcon(field.type)}
          {getFormFieldTypeLabel(field.type)}
        </span>
        <span className="mt-0.5 flex flex-1 items-center text-sm text-gray-500">
          {field.label}
          {field.label && field.required ? '*' : ''}
        </span>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onEdit()}
          className="invisible group-hover/form-field:visible"
        >
          <PencilIcon className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onDelete()}
          className="invisible group-hover/form-field:visible"
        >
          <TrashIcon className="h-4 w-4 text-red-600" />
        </Button>
      </div>
    );
  }
  return (
    <div className="flex w-full flex-col gap-4">
      <FormFieldItemForm
        field={field}
        fields={fields}
        readOnly={readOnly}
        showAdvanced={showAdvanced}
        onChange={(f) => {
          onChange(f);
        }}
      />
      <div className="flex justify-end gap-1">
        <Button size="icon-sm" onClick={onDone} variant="ghost">
          <CheckIcon className="h-4 w-4 text-green-500" />
        </Button>
        <Button size="icon-sm" onClick={() => onDelete()} variant="ghost">
          <TrashIcon className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

interface FormFieldItemFormProps {
  field: EditableFormField;
  fields: EditableFormField[];
  readOnly: boolean;
  showAdvanced?: boolean;
  onChange: (_: EditableFormField) => void;
}

export function FormFieldItemForm({
  field,
  fields,
  readOnly,
  showAdvanced,
  onChange,
}: FormFieldItemFormProps) {
  const supportedValidations = ['regex', 'length'];
  const hasValidations = new Set(field.validations?.map((v) => v.type));
  return (
    <div className="flex w-full flex-col gap-4">
      <Select
        value={field.type}
        disabled={readOnly}
        onValueChange={(e: FormFieldType) => {
          onChange({
            ...field,
            type: e,
          } as any);
        }}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Field type" />
        </SelectTrigger>
        <SelectContent>
          {formFieldTypes.map((type) => (
            <SelectItem key={type} value={type}>
              <span className="flex items-center gap-2">
                {getFormFieldTypeIcon(type)}
                {getFormFieldTypeLabel(type)}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        placeholder="label"
        title="Label"
        readOnly={readOnly}
        value={field.label || ''}
        onChange={(e) => {
          onChange({
            ...field,
            label: e.target.value,
          });
        }}
      />

      {showAdvanced && (
        <>
          <Input
            title="Id*"
            placeholder="id"
            readOnly={readOnly}
            value={field.id || ''}
            validation={{
              status:
                fields.filter((_field) => _field.id === field.id).length > 1 ||
                !field.id ||
                !/^[A-Za-z0-9]+$/.test(field.id)
                  ? 'error'
                  : undefined,
              message:
                fields.filter((_field) => _field.id === field.id).length > 1
                  ? 'You already have a field with this id'
                  : !field.id
                    ? 'Id is required'
                    : !/^[A-Za-z0-9]+$/.test(field.id)
                      ? 'Id must contain only letters and numbers'
                      : undefined,
            }}
            onChange={(e) => {
              onChange({
                ...field,
                id: e.target.value.trim(),
              });
            }}
          />

          {(field.type === 'businessEmail' ||
            field.type === 'email' ||
            field.type === 'hidden' ||
            field.type === 'number' ||
            field.type === 'state' ||
            field.type === 'country' ||
            field.type === 'tel' ||
            field.type === 'text' ||
            field.type === 'textArea') && (
            <Input
              placeholder="default value"
              title="Default value"
              readOnly={readOnly}
              value={field.defaultValue || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  defaultValue: e.target.value,
                });
              }}
            />
          )}

          {(field.type === 'select' ||
            field.type === 'multiChoice' ||
            field.type === 'pictureChoice') && (
            <div className="flex flex-col justify-start gap-1">
              <FieldTitle title="Default value" />
              <Select
                value={field.defaultValue || 'none'}
                disabled={readOnly}
                onValueChange={(e: string) => {
                  onChange({
                    ...field,
                    defaultValue: e === 'none' ? undefined : e,
                  } as any);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="default value" />
                </SelectTrigger>
                <SelectContent>
                  {field.defaultValue && (
                    <SelectItem value="none">None</SelectItem>
                  )}
                  {field.options
                    ?.map((o) =>
                      typeof o === 'string' ? { id: o, label: o, value: o } : o,
                    )
                    .filter((option) => option.value)
                    ?.map((option) => (
                      <SelectItem key={option.id} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {field.type !== 'consent' && (
            <Input
              placeholder="placeholder"
              title="Placeholder"
              readOnly={readOnly}
              value={field.placeholder || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  placeholder: e.target.value,
                });
              }}
            />
          )}
          <Input
            placeholder="error message"
            title="Custom error message"
            readOnly={readOnly}
            value={field.errorMessage || ''}
            onChange={(e) => {
              onChange({
                ...field,
                errorMessage: e.target.value,
              });
            }}
          />
        </>
      )}
      {(field.type === 'select' ||
        field.type === 'multiChoice' ||
        field.type === 'pictureChoice') && (
        <OptionsBuilder field={field} onChange={onChange} />
      )}
      {(field.type === 'multiChoice' || field.type === 'pictureChoice') && (
        <div className="flex items-center space-x-3">
          <Switch
            checked={field.multiple}
            onCheckedChange={(checked) => {
              if (readOnly) return;
              onChange({
                ...field,
                multiple: checked,
              });
            }}
          />
          <Label className="text-sm font-medium">Allow multiple choices</Label>
        </div>
      )}

      {(field.type === 'multiChoice' || field.type === 'pictureChoice') &&
        field.multiple && (
          <>
            <Input
              title="Minimum Selections"
              placeholder="Minimum Selections"
              readOnly={readOnly}
              type="number"
              value={field.minSelections?.toString() || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  minSelections: parseInt(e.target.value) || undefined,
                });
              }}
            />
            <Input
              title="Maximum Selections"
              placeholder="Maximum Selections"
              readOnly={readOnly}
              type="number"
              value={field.maxSelections?.toString() || ''}
              onChange={(e) => {
                onChange({
                  ...field,
                  maxSelections: parseInt(e.target.value) || undefined,
                });
              }}
            />
          </>
        )}
      {field.type === 'tel' && (
        <>
          <div className="flex items-center gap-2">
            <Checkbox
              id={'hide-country-codes-' + field._id}
              name={'hide-country-codes-' + field._id}
              checked={field.hideCountryCode || false}
              onCheckedChange={(checked) => {
                if (readOnly) return;
                onChange({
                  ...field,
                  hideCountryCode: checked as boolean,
                });
              }}
            />
            <label
              className="block text-sm leading-6 font-medium text-gray-900"
              htmlFor={'hide-country-codes-' + field._id}
            >
              Hide country codes
            </label>
          </div>
          {!field.hideCountryCode && (
            <>
              <ChipsAutocomplete<(typeof phoneCodes)[0]>
                title="Preferred Countries"
                getValue={(c) => `${c.name} ${c.code}`}
                renderItem={(c) => (
                  <span className="flex items-center gap-2">
                    <span className="text-sm">{c.flag}</span>
                    <span className="text-sm">{c.name}</span>
                    <span className="text-xs text-gray-500">{c.code}</span>
                  </span>
                )}
                selected={
                  (field.preferredCountryCodes ?? ['+1'])
                    .map((c) => phoneCodes.find((pc) => pc.code === c))
                    .filter(Boolean) as typeof phoneCodes
                }
                onChange={(s) => {
                  if (readOnly) return;
                  onChange({
                    ...field,
                    preferredCountryCodes: s.map((c) => c.code),
                  });
                }}
                getOptionId={(t) => t.code}
                options={phoneCodes}
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  id={'show-only-preferred-countries-' + field._id}
                  name={'show-only-preferred-countries-' + field._id}
                  checked={field.showOnlyPreferredCountries || false}
                  onCheckedChange={(checked) => {
                    if (readOnly) return;
                    onChange({
                      ...field,
                      showOnlyPreferredCountries: checked as boolean,
                    });
                  }}
                />
                <label
                  className="block text-sm leading-6 font-medium text-gray-900"
                  htmlFor={'hide-country-codes-' + field._id}
                >
                  Show only preferred countries
                </label>
              </div>
            </>
          )}
        </>
      )}
      {field.type === 'address' && (
        <AddressFieldConfig
          field={field as EditableFormField & AddressFormField}
          readOnly={readOnly}
          onChange={onChange}
        />
      )}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id={'required-' + field._id}
            name={'required-' + field._id}
            checked={field.required || false}
            onCheckedChange={(checked) => {
              if (readOnly) return;
              onChange({
                ...field,
                required: checked as boolean,
              });
            }}
          />
          <label
            className="block text-sm leading-6 font-medium text-gray-900"
            htmlFor={'required-' + field._id}
          >
            Required
          </label>
        </div>
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-2">
            <Checkbox
              id={'protected-' + field._id}
              name={'protected-' + field._id}
              checked={field.protected || false}
              onCheckedChange={(checked) => {
                if (readOnly) return;
                onChange({
                  ...field,
                  protected: checked as boolean,
                });
              }}
            />
            <label
              className="block text-sm leading-6 font-medium text-gray-900"
              htmlFor={'protected-' + field._id}
            >
              Protected
            </label>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <QuestionMarkCircleIcon className="size-4 cursor-help text-gray-400 transition-colors hover:text-gray-600" />
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p>
                Data entered here will be forwarded to your webhook and not
                stored in our system.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {(field.type === 'text' ||
        field.type === 'number' ||
        field.type === 'tel') && (
        <div className="flex flex-col items-start gap-4">
          {field.validations?.map((v) => (
            <div key={v.type} className="flex w-full items-center gap-2">
              {v.type === 'regex' ? (
                <Input
                  placeholder="regex pattern"
                  title="Regex pattern"
                  readOnly={readOnly}
                  className="w-full"
                  value={v.regex || ''}
                  onChange={(e) => {
                    onChange({
                      ...field,
                      validations: field.validations?.map((val) =>
                        val.type === 'regex'
                          ? { ...val, regex: e.target.value }
                          : val,
                      ) || [{ type: 'regex', regex: e.target.value }],
                    });
                  }}
                />
              ) : (
                <div className="flex flex-row gap-2">
                  <Input
                    placeholder="Min length"
                    title="Min"
                    readOnly={readOnly}
                    className="w-full"
                    type="number"
                    value={v.min?.toString() || ''}
                    onChange={(e) => {
                      onChange({
                        ...field,
                        validations: field.validations?.map((val) =>
                          val.type === 'length'
                            ? {
                                ...val,
                                min: parseInt(e.target.value) || undefined,
                              }
                            : val,
                        ) || [
                          {
                            type: 'length',
                            min: parseInt(e.target.value) || undefined,
                            max: undefined,
                          },
                        ],
                      });
                    }}
                  />
                  <Input
                    placeholder="Max length"
                    title="Max"
                    readOnly={readOnly}
                    type="number"
                    className="w-full"
                    value={v.max?.toString() || ''}
                    onChange={(e) => {
                      onChange({
                        ...field,
                        validations: field.validations?.map((val) =>
                          val.type === 'length'
                            ? {
                                ...val,
                                max: parseInt(e.target.value) || undefined,
                              }
                            : val,
                        ) || [
                          {
                            type: 'length',
                            min: undefined,
                            max: parseInt(e.target.value) || undefined,
                          },
                        ],
                      });
                    }}
                  />
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => {
                      onChange({
                        ...field,
                        validations:
                          field.validations?.filter(
                            (val) => val.type !== v.type,
                          ) || [],
                      });
                    }}
                  >
                    <XCircleIcon className="h-4 w-4 text-red-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Remove validation</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ))}
          {supportedValidations.length > hasValidations.size && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="micro" variant="ghost" className="-ms-2">
                  <PlusIcon className="h-4 w-4" />
                  Add validation
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {supportedValidations
                  .filter((v) => !hasValidations.has(v as any))
                  .map((v) => (
                    <DropdownMenuItem
                      key={v}
                      onClick={() => {
                        onChange({
                          ...field,
                          validations: [
                            ...(field.validations || []),
                            v === 'regex'
                              ? { type: 'regex', regex: '' }
                              : {
                                  type: 'length',
                                  min: undefined,
                                  max: undefined,
                                },
                          ],
                        });
                      }}
                    >
                      {v === 'regex' ? 'Regex validation' : 'Length validation'}
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  );
}

type EditableOptionsFormField = (
  | SelectFormField
  | MultiChoiceFormField
  | PictureChoiceFormField
) & {
  _id: string;
};
interface OptionsBuilderProps {
  field: EditableOptionsFormField;
  onChange: (_: EditableOptionsFormField) => void;
}

const OptionsBuilder = ({ field, onChange }: OptionsBuilderProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between">
        <span className="text-sm font-medium text-gray-900">Options</span>
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={() => {
            onChange({
              ...field,
              options: [
                ...((field.options || []) as any),
                { value: '', label: '', id: uid() },
              ],
            });
          }}
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
      </div>
      <SortableList
        items={
          field.options?.map((o) => ({
            ...(typeof o === 'string' ? { value: o, label: o, id: uid() } : o),
          })) || []
        }
        hideDivider
        hideDragHandle
        renderItemContent={(option, index) => {
          const _option =
            typeof option === 'string'
              ? { value: option, label: option }
              : option;
          return (
            <div className="group flex w-full flex-col items-stretch gap-2 rounded-md bg-white px-4 py-2 shadow-sm">
              <Button
                size="icon-sm"
                className="absolute top-2 right-2 z-10 hidden group-hover:block"
                variant="ghost"
                onClick={() => {
                  onChange({
                    ...field,
                    options: [
                      ...(field.options || []).filter((_, i) => i !== index),
                    ] as any,
                  });
                }}
              >
                <TrashIcon className="h-4 w-4 text-red-500" />
              </Button>
              <Input
                placeholder="Option label"
                title="Label"
                readOnly={false}
                value={_option.label}
                onChange={(e) => {
                  onChange({
                    ...field,
                    options: [
                      ...(field.options || []).map((o, i) =>
                        i === index
                          ? {
                              ..._option,
                              label: e.target.value,
                              value:
                                !_option.value ||
                                _option.value === _option.label
                                  ? e.target.value
                                  : _option.value,
                            }
                          : o,
                      ),
                    ] as any,
                  });
                }}
              />
              <Input
                placeholder="Option value"
                title="Value"
                readOnly={false}
                value={_option.value}
                onChange={(e) => {
                  onChange({
                    ...field,
                    options: [
                      ...(field.options || []).map((o, i) =>
                        i === index ? { ..._option, value: e.target.value } : o,
                      ),
                    ] as any,
                  });
                }}
              />
              {field.type === 'pictureChoice' && (
                <>
                  <Input
                    placeholder="Description"
                    title="Description (optional)"
                    readOnly={false}
                    value={
                      (
                        option as NonNullable<
                          PictureChoiceFormField['options']
                        >[0]
                      ).description
                    }
                    onChange={(e) => {
                      onChange({
                        ...field,
                        options: [
                          ...(field.options || []).map((o, i) =>
                            i === index
                              ? { ..._option, description: e.target.value }
                              : o,
                          ),
                        ] as any,
                      });
                    }}
                  />
                  <FieldTitle title="Image" />
                  <FileUploadInput
                    showImageSettings
                    types={['image']}
                    image={
                      (
                        option as NonNullable<
                          PictureChoiceFormField['options']
                        >[0]
                      ).image
                    }
                    onSelected={(image) => {
                      onChange({
                        ...field,
                        options: [
                          ...(field.options || []).map((o, i) =>
                            i === index
                              ? { ..._option, image: image || null }
                              : o,
                          ),
                        ] as any,
                      });
                    }}
                  />
                </>
              )}
            </div>
          );
        }}
        onChange={(items) => {
          onChange({
            ...field,
            options: items.map(
              (o) =>
                (typeof o === 'string'
                  ? { value: o, label: o, id: uid() }
                  : o) as any,
            ),
          });
        }}
      />
    </div>
  );
};

// Address place types for configuration
const addressPlaceTypes: { value: AddressPlaceType; label: string }[] = [
  { value: 'address', label: 'Address' },
  { value: 'geocode', label: 'Geocode' },
  { value: 'establishment', label: 'Establishment' },
  { value: '(regions)', label: 'Regions' },
  { value: '(cities)', label: 'Cities' },
];

// Address component visibility options
const addressComponentVisibilityOptions: {
  value: AddressComponentVisibility;
  label: string;
}[] = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'optional', label: 'Optional' },
  { value: 'required', label: 'Required' },
];

// Address components for configuration
const addressComponents: {
  key: keyof AddressComponentConfig;
  label: string;
}[] = [
  { key: 'streetNumber', label: 'Street Number' },
  { key: 'route', label: 'Street' },
  { key: 'secondaryLine', label: 'Apt/Suite/Unit' },
  { key: 'locality', label: 'City' },
  { key: 'administrativeAreaLevel1', label: 'State/Province' },
  { key: 'postalCode', label: 'Postal Code' },
  { key: 'country', label: 'Country' },
];

interface AddressFieldConfigProps {
  field: EditableFormField & AddressFormField;
  readOnly: boolean;
  onChange: (_: EditableFormField) => void;
}

const AddressFieldConfig = ({
  field,
  readOnly,
  onChange,
}: AddressFieldConfigProps) => {
  return (
    <div className="flex flex-col gap-4">
      <ChipsAutocomplete<(typeof countries)[0]>
        title="Restrict to Countries"
        getValue={(c) => `${c.name} (${c.code})`}
        renderItem={(c) => (
          <span className="flex items-center gap-2">
            <span className="text-sm">{c.name}</span>
            <span className="text-xs text-gray-500">{c.code}</span>
          </span>
        )}
        selected={
          (field.restrictCountries ?? [])
            .map((code: string) => countries.find((c) => c.code === code))
            .filter(Boolean) as typeof countries
        }
        onChange={(s) => {
          if (readOnly) return;
          onChange({
            ...field,
            restrictCountries: s.map((c) => c.code),
          });
        }}
        getOptionId={(c) => c.code}
        options={countries}
      />

      <div className="flex flex-col gap-1">
        <FieldTitle title="Place Types" />
        <div className="flex flex-wrap gap-2">
          {addressPlaceTypes.map((placeType) => {
            const isSelected = (field.types ?? ['address']).includes(
              placeType.value,
            );
            return (
              <button
                key={placeType.value}
                type="button"
                onClick={() => {
                  if (readOnly) return;
                  const currentTypes = field.types ?? ['address'];
                  const newTypes = isSelected
                    ? currentTypes.filter(
                        (t: AddressPlaceType) => t !== placeType.value,
                      )
                    : [...currentTypes, placeType.value];
                  onChange({
                    ...field,
                    types: newTypes.length > 0 ? newTypes : ['address'],
                  });
                }}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isSelected
                    ? 'bg-brand-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {placeType.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id={'show-secondary-line-' + field._id}
          name={'show-secondary-line-' + field._id}
          checked={field.showSecondaryLine || false}
          onCheckedChange={(checked) => {
            if (readOnly) return;
            onChange({
              ...field,
              showSecondaryLine: checked as boolean,
            });
          }}
        />
        <label
          className="block text-sm leading-6 font-medium text-gray-900"
          htmlFor={'show-secondary-line-' + field._id}
        >
          Show apartment/suite field
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id={'editable-after-selection-' + field._id}
          name={'editable-after-selection-' + field._id}
          checked={field.editableAfterSelection || false}
          onCheckedChange={(checked) => {
            if (readOnly) return;
            onChange({
              ...field,
              editableAfterSelection: checked as boolean,
            });
          }}
        />
        <label
          className="block text-sm leading-6 font-medium text-gray-900"
          htmlFor={'editable-after-selection-' + field._id}
        >
          Allow editing address components after selection
        </label>
      </div>

      {field.editableAfterSelection && (
        <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-3">
          <FieldTitle title="Component Visibility" />
          <div className="grid grid-cols-2 gap-2">
            {addressComponents.map((comp) => (
              <div key={String(comp.key)} className="flex items-center gap-2">
                <span className="min-w-[100px] text-sm text-gray-600">
                  {comp.label}
                </span>
                <Select
                  value={field.componentConfig?.[comp.key] ?? 'optional'}
                  onValueChange={(value: AddressComponentVisibility) => {
                    if (readOnly) return;
                    onChange({
                      ...field,
                      componentConfig: {
                        ...field.componentConfig,
                        [comp.key]: value,
                      },
                    });
                  }}
                >
                  <SelectTrigger className="h-8 w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {addressComponentVisibilityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Checkbox
          id={'allow-manual-entry-' + field._id}
          name={'allow-manual-entry-' + field._id}
          checked={field.allowManualEntry || false}
          onCheckedChange={(checked) => {
            if (readOnly) return;
            onChange({
              ...field,
              allowManualEntry: checked as boolean,
            });
          }}
        />
        <label
          className="block text-sm leading-6 font-medium text-gray-900"
          htmlFor={'allow-manual-entry-' + field._id}
        >
          Allow manual entry when address not found
        </label>
      </div>

      <div className="flex flex-col gap-1">
        <FieldTitle title="Language (optional)" />
        <Select
          value={field.language || 'auto'}
          disabled={readOnly}
          onValueChange={(value: string) => {
            onChange({
              ...field,
              language: value === 'auto' ? '' : value,
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Default (English)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Default (English)</SelectItem>
            {languages.map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                {lang.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
